import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Share2, HeartHandshake, Laptop, GraduationCap, Sprout } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { LINKS, IMAGES } from "@/data/content";

const PILLARS = [
  { icon: Globe2, title: "Uso del club", text: "Prima di tutto sono una viaggiatrice: uso la piattaforma per i miei viaggi e la conosco dall'interno." },
  { icon: Share2, title: "Condivisione", text: "Racconto quello che scopro, con onestà. Se qualcosa può essere utile ad altri, lo condivido." },
  { icon: HeartHandshake, title: "Relazioni", text: "Questo percorso è fatto di persone: ascolto, confronto, fiducia costruita nel tempo." },
  { icon: Laptop, title: "Lavoro online", text: "Lavoro da dove voglio, con gli strumenti digitali che sto imparando a usare ogni giorno." },
  { icon: GraduationCap, title: "Formazione", text: "Corsi, eventi, mentorship: la formazione continua è il motore di tutto il percorso." },
  { icon: Sprout, title: "Crescita personale", text: "Ogni sfida — un video, una call, un palco — è un'occasione per diventare una versione migliore di me." },
];

export default function Lavoro() {
  return (
    <div data-testid="lavoro-page">
      <Seo
        title="Business e networking nel settore dei viaggi | Laure Hila"
        description="Un nuovo modo di lavorare: dopo 25 anni da dipendente, un percorso imprenditoriale nel settore dei viaggi tra digitale, formazione, networking e crescita personale."
      />

      <section className="pt-32 md:pt-44 pb-14 md:pb-20 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Il mio percorso</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Un nuovo modo
            </MaskedLine>
            <MaskedLine delay={0.25} className="text-5xl md:text-7xl italic text-gold">
              di lavorare
            </MaskedLine>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-2xl font-serif text-xl md:text-2xl italic leading-relaxed text-ink/85">
              «Cercavo qualcosa che potesse adattarsi alla mia vita, ai miei tempi
              e ai miei obiettivi.»
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-stone">
                Dopo tanti anni con orari decisi da altri, volevo un'attività che
                potesse crescere insieme a me — non il contrario. Ho iniziato a
                esplorare un modo diverso di lavorare, imparando
                progressivamente digitale, turismo, networking, formazione e
                comunicazione.
              </p>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-stone">
                Non è una scorciatoia e non promette risultati economici: è un
                percorso che richiede impegno, costanza e voglia di imparare.
                Esattamente come ogni cosa che vale.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="frame-gold">
              <div className="relative z-10 overflow-hidden rounded-xl">
                <img
                  src={IMAGES.mwrEvento.url}
                  alt={IMAGES.mwrEvento.alt}
                  className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-ink mb-10 md:mb-14">
              Cosa <span className="italic text-gold">comprende</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div
                  data-testid={`lavoro-pillar-${i}`}
                  className="h-full p-7 md:p-8 rounded-xl bg-sand/70 border border-line hover:border-gold transition-colors duration-500 group"
                >
                  <p.icon
                    size={28}
                    className="text-gold group-hover:scale-110 transition-transform duration-300"
                    strokeWidth={1.5}
                  />
                  <h3 className="mt-5 font-serif text-2xl text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-stone">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
              Ti incuriosisce? <span className="italic text-gold">Parliamone</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg text-stone leading-relaxed">
              Ti racconto come funziona davvero, con i tempi e le domande che
              servono. Senza impegno.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={LINKS.mwrLife}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lavoro-cta-percorso"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300 group"
              >
                Scopri il percorso
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <a
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lavoro-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-ink/25 text-ink font-semibold text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Parliamone su WhatsApp
              </a>
              <Link
                to="/contatti"
                data-testid="lavoro-cta-contatti"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-ink/25 text-ink font-semibold text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300 group"
              >
                Scrivimi dal sito
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
